import type { ProductSummary } from "@cabal/shared/domain/session/product/types.js";
import {
  TRACKED_PRODUCT_FIELD,
  type TrackedProductField,
} from "@/types/frontend-types";

export interface ProductMergeProductFlag {
  productId: string;
  fields: TrackedProductField[];
}

export interface ProductMergeVersionArrivalFlag {
  versionId: string;
  scopeProductId: string;
}

export interface ProductMergeFileArrivalFlag {
  fileId: string;
  scopeProductId: string;
}

export interface ProductMergeAncestorFlag {
  ancestorId: string;
  originId: string;
}

export interface ProductMergeOrphanChildInsert {
  childId: string;
  parentId: string;
}

export interface ProductMergeFlashSelection {
  productFlags: ProductMergeProductFlag[];
  versionArrivalFlags: ProductMergeVersionArrivalFlag[];
  fileArrivalFlags: ProductMergeFileArrivalFlag[];
  ancestorSubproductFlags: ProductMergeAncestorFlag[];
  ancestorFileFlags: ProductMergeAncestorFlag[];
  orphanChildInserts: ProductMergeOrphanChildInsert[];
}

export interface SelectProductMergeFlashSelectionInput {
  items: readonly ProductSummary[];
  productsById: Readonly<Record<string, ProductSummary>>;
  canFlash: boolean;
  userAttachmentsProductId: string | null;
}

interface SelectProductMergeFlashInput {
  product: ProductSummary;
  productsById: Readonly<Record<string, ProductSummary>>;
  canFlash: boolean;
  userAttachmentsProductId: string | null;
}

interface SelectProductChangeInput {
  previous: ProductSummary | undefined;
  product: ProductSummary;
  canFlash: boolean;
}

interface ProductChangeComparison {
  oldProduct: ProductSummary;
  newProduct: ProductSummary;
}

interface ProductFieldChangeDetector {
  field: TrackedProductField;
  hasChanged: (input: ProductChangeComparison) => boolean;
}

interface StringListComparison {
  oldValues: readonly string[];
  newValues: readonly string[];
}

interface SelectProductFlagsInput {
  product: ProductSummary;
  changes: ReadonlySet<TrackedProductField>;
  canFlash: boolean;
}

interface SelectOrphanChildInsertsInput {
  product: ProductSummary;
  previous: ProductSummary | undefined;
  productsById: Readonly<Record<string, ProductSummary>>;
}

interface SelectVersionArrivalFlagsInput {
  product: ProductSummary;
  previous: ProductSummary | undefined;
  changes: ReadonlySet<TrackedProductField>;
  canFlash: boolean;
}

interface SelectSubproductAncestorFlagsInput
  extends SelectVersionArrivalFlagsInput {
  productsById: Readonly<Record<string, ProductSummary>>;
  userAttachmentsProductId: string | null;
}

interface FileArrivalSelection {
  fileArrivalFlags: ProductMergeFileArrivalFlag[];
  ancestorFileFlags: ProductMergeAncestorFlag[];
}

type SelectFileArrivalSelectionInput = SelectSubproductAncestorFlagsInput;

interface SelectFileArrivalBaselineInput {
  previous: ProductSummary | undefined;
  changes: ReadonlySet<TrackedProductField>;
  canFlash: boolean;
}

interface SelectAncestorFileFlagsInput {
  productId: string;
  newFileIds: readonly string[];
  productsById: Readonly<Record<string, ProductSummary>>;
  userAttachmentsProductId: string | null;
}

interface SelectAncestorChainInput {
  productId: string;
  productsById: Readonly<Record<string, ProductSummary>>;
}

interface UserAttachmentsSubtreeInput extends SelectAncestorChainInput {
  userAttachmentsProductId: string | null;
}

const PRODUCT_FIELD_CHANGE_DETECTORS: readonly ProductFieldChangeDetector[] = [
  {
    field: TRACKED_PRODUCT_FIELD.Name,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      oldProduct.name !== newProduct.name,
  },
  {
    field: TRACKED_PRODUCT_FIELD.Type,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      oldProduct.type !== newProduct.type,
  },
  {
    field: TRACKED_PRODUCT_FIELD.Role,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      oldProduct.role !== newProduct.role,
  },
  {
    field: TRACKED_PRODUCT_FIELD.Status,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      oldProduct.status !== newProduct.status,
  },
  {
    field: TRACKED_PRODUCT_FIELD.Owner,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      oldProduct.owner?.actorId !== newProduct.owner?.actorId,
  },
  {
    field: TRACKED_PRODUCT_FIELD.CollabCount,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      oldProduct.collabCount !== newProduct.collabCount,
  },
  {
    field: TRACKED_PRODUCT_FIELD.VersionCount,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      oldProduct.versionCount !== newProduct.versionCount,
  },
  {
    field: TRACKED_PRODUCT_FIELD.SubproductCount,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      oldProduct.subproductCount !== newProduct.subproductCount,
  },
  {
    field: TRACKED_PRODUCT_FIELD.LatestVersionNumber,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      oldProduct.latestVersionNumber !== newProduct.latestVersionNumber,
  },
  {
    field: TRACKED_PRODUCT_FIELD.VersionIds,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      !areStringListsEqual({
        oldValues: oldProduct.versionIds,
        newValues: newProduct.versionIds,
      }),
  },
  {
    field: TRACKED_PRODUCT_FIELD.AttachedFiles,
    hasChanged: ({ oldProduct, newProduct }): boolean =>
      getAttachedFileSignature(oldProduct) !==
      getAttachedFileSignature(newProduct),
  },
];

export function selectProductMergeFlashSelection({
  items,
  productsById,
  canFlash,
  userAttachmentsProductId,
}: SelectProductMergeFlashSelectionInput): ProductMergeFlashSelection {
  const selection = createEmptyProductMergeFlashSelection();

  items.forEach((product) => {
    const productSelection = selectProductMergeFlash({
      productsById,
      canFlash,
      userAttachmentsProductId,
      product,
    });
    selection.productFlags.push(...productSelection.productFlags);
    selection.versionArrivalFlags.push(...productSelection.versionArrivalFlags);
    selection.fileArrivalFlags.push(...productSelection.fileArrivalFlags);
    selection.ancestorSubproductFlags.push(
      ...productSelection.ancestorSubproductFlags,
    );
    selection.ancestorFileFlags.push(...productSelection.ancestorFileFlags);
    selection.orphanChildInserts.push(...productSelection.orphanChildInserts);
  });

  return selection;
}

function createEmptyProductMergeFlashSelection(): ProductMergeFlashSelection {
  return {
    productFlags: [],
    versionArrivalFlags: [],
    fileArrivalFlags: [],
    ancestorSubproductFlags: [],
    ancestorFileFlags: [],
    orphanChildInserts: [],
  };
}

function selectProductMergeFlash({
  product,
  productsById,
  canFlash,
  userAttachmentsProductId,
}: SelectProductMergeFlashInput): ProductMergeFlashSelection {
  const previous = productsById[product.id];
  const changes = selectProductChanges({ previous, product, canFlash });
  const fileArrivalSelection = selectFileArrivalSelection({
    product,
    previous,
    changes,
    canFlash,
    productsById,
    userAttachmentsProductId,
  });

  return {
    productFlags: selectProductFlags({ product, changes, canFlash }),
    versionArrivalFlags: selectVersionArrivalFlags({
      product,
      previous,
      changes,
      canFlash,
    }),
    fileArrivalFlags: fileArrivalSelection.fileArrivalFlags,
    ancestorSubproductFlags: selectSubproductAncestorFlags({
      product,
      previous,
      changes,
      canFlash,
      productsById,
      userAttachmentsProductId,
    }),
    ancestorFileFlags: fileArrivalSelection.ancestorFileFlags,
    orphanChildInserts: selectOrphanChildInserts({
      product,
      previous,
      productsById,
    }),
  };
}

function selectProductChanges({
  previous,
  product,
  canFlash,
}: SelectProductChangeInput): Set<TrackedProductField> {
  if (!canFlash) return new Set<TrackedProductField>();
  return detectProductChanges(previous, product);
}

function selectProductFlags({
  product,
  changes,
  canFlash,
}: SelectProductFlagsInput): ProductMergeProductFlag[] {
  if (!canFlash || changes.size === 0) return [];
  return [{ productId: product.id, fields: [...changes] }];
}

function selectOrphanChildInserts({
  product,
  previous,
  productsById,
}: SelectOrphanChildInsertsInput): ProductMergeOrphanChildInsert[] {
  if (previous) return [];

  const parentId = product.parentProductId;
  if (!parentId) return [];
  if (!productsById[parentId]) return [];

  return [{ childId: product.id, parentId }];
}

function selectVersionArrivalFlags({
  product,
  previous,
  changes,
  canFlash,
}: SelectVersionArrivalFlagsInput): ProductMergeVersionArrivalFlag[] {
  if (!canFlash) return [];
  if (!previous) return [];
  if (!changes.has(TRACKED_PRODUCT_FIELD.VersionIds)) return [];

  const previousVersionIds = new Set(previous.versionIds);
  return product.versionIds
    .filter((versionId) => !previousVersionIds.has(versionId))
    .map((versionId) => ({ versionId, scopeProductId: product.id }));
}

function selectSubproductAncestorFlags({
  product,
  previous,
  changes,
  canFlash,
  productsById,
  userAttachmentsProductId,
}: SelectSubproductAncestorFlagsInput): ProductMergeAncestorFlag[] {
  if (!shouldFlagSubproductAncestors({ product, previous, changes, canFlash })) {
    return [];
  }
  if (
    isInUserAttachmentsSubtreeForSnapshot({
      productId: product.id,
      productsById,
      userAttachmentsProductId,
    })
  ) {
    return [];
  }

  return selectAncestorChain({ productId: product.id, productsById }).map(
    (ancestorId) => ({ ancestorId, originId: product.id }),
  );
}

function shouldFlagSubproductAncestors({
  product,
  previous,
  changes,
  canFlash,
}: SelectVersionArrivalFlagsInput): boolean {
  if (!canFlash) return false;
  if (!previous) return false;
  if (!changes.has(TRACKED_PRODUCT_FIELD.SubproductCount)) return false;
  return product.subproductCount > previous.subproductCount;
}

function selectFileArrivalSelection({
  product,
  previous,
  changes,
  canFlash,
  productsById,
  userAttachmentsProductId,
}: SelectFileArrivalSelectionInput): FileArrivalSelection {
  const fileArrivalBaseline = selectFileArrivalBaseline({
    previous,
    changes,
    canFlash,
  });
  if (!fileArrivalBaseline) return createEmptyFileArrivalSelection();

  const previousFileIds = new Set(
    (fileArrivalBaseline.attachedFiles ?? []).map((file) => file.id),
  );
  const newFileIds = (product.attachedFiles ?? [])
    .filter((file) => !previousFileIds.has(file.id))
    .map((file) => file.id);

  return {
    fileArrivalFlags: newFileIds.map((fileId) => ({
      fileId,
      scopeProductId: product.id,
    })),
    ancestorFileFlags: selectAncestorFileFlags({
      productId: product.id,
      newFileIds,
      productsById,
      userAttachmentsProductId,
    }),
  };
}

function selectFileArrivalBaseline({
  previous,
  changes,
  canFlash,
}: SelectFileArrivalBaselineInput): ProductSummary | null {
  if (!canFlash) return null;
  if (!previous) return null;
  if (!changes.has(TRACKED_PRODUCT_FIELD.AttachedFiles)) return null;
  return previous;
}

function createEmptyFileArrivalSelection(): FileArrivalSelection {
  return {
    fileArrivalFlags: [],
    ancestorFileFlags: [],
  };
}

function selectAncestorFileFlags({
  productId,
  newFileIds,
  productsById,
  userAttachmentsProductId,
}: SelectAncestorFileFlagsInput): ProductMergeAncestorFlag[] {
  if (newFileIds.length === 0) return [];
  if (
    isInUserAttachmentsSubtreeForSnapshot({
      productId,
      productsById,
      userAttachmentsProductId,
    })
  ) {
    return [];
  }

  return selectAncestorChain({ productId, productsById }).map((ancestorId) => ({
    ancestorId,
    originId: productId,
  }));
}

function selectAncestorChain({
  productId,
  productsById,
}: SelectAncestorChainInput): string[] {
  const chain: string[] = [];
  let current = productsById[productId];
  while (current?.parentProductId) {
    chain.push(current.parentProductId);
    current = productsById[current.parentProductId];
  }
  return chain;
}

function isInUserAttachmentsSubtreeForSnapshot({
  productId,
  productsById,
  userAttachmentsProductId,
}: UserAttachmentsSubtreeInput): boolean {
  if (!userAttachmentsProductId) return false;
  if (productId === userAttachmentsProductId) return true;
  return selectAncestorChain({ productId, productsById }).includes(
    userAttachmentsProductId,
  );
}

function detectProductChanges(
  oldProduct: ProductSummary | undefined,
  newProduct: ProductSummary,
): Set<TrackedProductField> {
  if (!oldProduct) {
    return new Set<TrackedProductField>();
  }

  return new Set(
    PRODUCT_FIELD_CHANGE_DETECTORS.filter((detector) =>
      detector.hasChanged({ oldProduct, newProduct }),
    ).map((detector) => detector.field),
  );
}

function areStringListsEqual({
  oldValues,
  newValues,
}: StringListComparison): boolean {
  return JSON.stringify(oldValues) === JSON.stringify(newValues);
}

function getAttachedFileSignature(product: ProductSummary): string {
  return (product.attachedFiles ?? [])
    .map((file) => file.id)
    .join("|");
}
